import axios from 'axios';

import events, { eventEmitter } from '@/events';
import logger from '@/utils/logger';

import { generateAzureB2CCodeVerifierAndCodeChallenge, randomCode } from '../utils/azureB2C';
import { getAzureB2CPolicyName, getCodeVerifierAndRemoveFromLocalStorage } from '../utils/localStorage/authentication';

import { getCurrentCountryCode } from './countries';

import { MY_UPLINK_CLIENT_ID, REQ_CONFIG_FORM } from './index';

export const AZURE_B2C_TENANT_ID = process.env.AZURE_B2C_TENANT_ID || '';
export const AZURE_B2C_HOST_NAME = process.env.AZURE_B2C_HOST_NAME || '';
export const AZURE_B2C_CLIENT_ID = process.env.AZURE_B2C_CLIENT_ID || '';
export const AZURE_B2C_POLICY_LOGIN = 'B2C_1A_LOGIN_FLOW_EMAIL_CONSUMERS';
export const AZURE_B2C_POLICY_PASSWORD_CHANGE = 'B2C_1A_PASSWORDCHANGE';
export const AZURE_B2C_POLICY_MFA_CHANGE = 'B2C_1A_MFACHANGE';
export const B2C_1A_EMAILCHANGE = 'B2C_1A_EMAILCHANGE';
export const AuthOptions = {
  SIGN_IN: 'signin',
  SIGN_UP: 'signup',
};

const azureB2CBaseUrl = `https://${AZURE_B2C_HOST_NAME}/${AZURE_B2C_TENANT_ID}.onmicrosoft.com`;
const azureB2CScope = `https://${AZURE_B2C_TENANT_ID}.onmicrosoft.com/api/READSYSTEM https://${AZURE_B2C_TENANT_ID}.onmicrosoft.com/api/WRITESYSTEM`;

/**
 * Generates and returns the Azure B2C login link with specified options.
 *
 * @async
 * @param {{redirectTo?: string, language?: string, authOption?: AuthOptions}} options - The options for generating the login link.
 * @returns {Promise<string>} The Azure B2C login link.
 */
export const getAzureB2CLoginLink = async ({
  redirectTo,
  language,
  authOption = AuthOptions.SIGN_IN,
  isTokenRefresh = false,
} = {}) => {
  try {
    const { codeChallenge } = await generateAzureB2CCodeVerifierAndCodeChallenge();
    const countryCode = getCurrentCountryCode();

    const baseUrl = `${azureB2CBaseUrl}/${AZURE_B2C_POLICY_LOGIN}/oauth2/v2.0/authorize`;
    const stateObj = { redirectTo: redirectTo || '', isTokenRefresh };
    const params = new URLSearchParams({
      client_id: AZURE_B2C_CLIENT_ID,
      response_type: 'code',
      redirect_uri: `${window.location.origin}/login`,
      response_mode: 'query',
      scope: azureB2CScope,
      state: JSON.stringify(stateObj),
      lang: language || '',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      clientId: MY_UPLINK_CLIENT_ID,
      countryCode: countryCode || '',
      authOption,
    });

    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    eventEmitter.emit(events.appInsights.logError, error);
  }
};

/**
 * Issues Azure B2C tokens using an authorization code.
 *
 * @param {string} code - The authorization code obtained after user authentication.
 * @returns {Promise} A Promise that resolves when the tokens are issued successfully.
 */
export const issueAzureB2CTokens = (code, policyId) => {
  try {
    const codeVerifier = getCodeVerifierAndRemoveFromLocalStorage();

    const baseUrl = `${azureB2CBaseUrl}/${policyId}/oauth2/v2.0/token`;
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: AZURE_B2C_CLIENT_ID,
      scope: policyId === AZURE_B2C_POLICY_PASSWORD_CHANGE ? `${azureB2CScope} offline_access` : azureB2CScope,
      code: code,
      redirect_uri: `${window.location.origin}/login`,
      code_verifier: codeVerifier,
    });

    return axios.post(baseUrl, body.toString(), REQ_CONFIG_FORM);
  } catch (error) {
    eventEmitter.emit(events.appInsights.logError, error);
  }
};

/**
 * Reissues Azure B2C tokens using a refresh token.
 *
 * @param {string} refreshToken - The refresh token used to obtain new tokens.
 * @returns {Promise} A Promise that resolves when the tokens are reissued successfully.
 */
export const reissueAzureB2CTokens = refreshToken => {
  try {
    const policyId = getAzureB2CPolicyName();
    const baseUrl = `${azureB2CBaseUrl}/${policyId}/oauth2/v2.0/token`;
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: AZURE_B2C_CLIENT_ID,
      refresh_token: refreshToken,
    });

    return axios.post(baseUrl, body.toString(), REQ_CONFIG_FORM);
  } catch (error) {
    eventEmitter.emit(events.appInsights.logError, error);
  }
};

/**
 *  Generates and returns the Azure B2C logout link.
 *
 * @returns {string} The Azure B2C logout link.
 */
export const getAzureB2CLogoutLink = () => {
  try {
    const baseUrl = `${azureB2CBaseUrl}/oauth2/v2.0/logout`;
    // Policy may be missing when logoutUser() is called from email confirmation pages
    // (delete/leave account) where no B2C login occurred in this browser session,
    // or when localStorage was cleared by a prior logout (stored as empty string "").
    // Fallback to login policy to ensure the `p` param is never empty — an empty value
    // causes Azure B2C to return "Resource removed / temporarily unavailable".
    const policyId = getAzureB2CPolicyName() || AZURE_B2C_POLICY_LOGIN;

    const params = new URLSearchParams({
      p: policyId,
      client_id: AZURE_B2C_CLIENT_ID,
      post_logout_redirect_uri: `${window.location.origin}/login`,
    });

    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    eventEmitter.emit(events.appInsights.logError, error);
  }
};

/**
 * Generates and returns the Azure B2C Change Password link.
 *
 * @async
 * @param {string} returnUri - The URL to redirect to after the password has been changed.
 * @returns {Promise<string>} The Azure B2C Change Password link.
 */
export const getAzureB2CChangePasswordLink = async (returnUri, language, spId) => {
  try {
    const { codeChallenge } = await generateAzureB2CCodeVerifierAndCodeChallenge();
    const baseUrl = `${azureB2CBaseUrl}/${AZURE_B2C_POLICY_PASSWORD_CHANGE}/oauth2/v2.0/authorize`;

    const params = new URLSearchParams({
      client_id: AZURE_B2C_CLIENT_ID,
      response_type: 'code',
      redirect_uri: returnUri,
      scope: `${azureB2CScope} offline_access`,
      state: randomCode(8),
      lang: language || '',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      clientId: MY_UPLINK_CLIENT_ID,
      servicePartnerId: spId ?? '',
    });

    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    // TODO: Raise notifiation
    logger.toAppInsights(error);
  }
};

/**
 * Generates and returns the Azure B2C Change Multi Factor auth link.
 *
 * @async
 * @param {string} returnUri - The URL to redirect to after the password has been changed.
 * @returns {Promise<string>} The Azure B2C Change MFA link.
 */
export const getAzureB2CChangeMFALink = async (returnUri, language) => {
  try {
    const { codeChallenge } = await generateAzureB2CCodeVerifierAndCodeChallenge();
    const baseUrl = `${azureB2CBaseUrl}/${AZURE_B2C_POLICY_MFA_CHANGE}/oauth2/v2.0/authorize`;

    const params = new URLSearchParams({
      client_id: AZURE_B2C_CLIENT_ID,
      response_type: 'code',
      response_mode: 'query',
      redirect_uri: returnUri,
      scope: `${azureB2CScope} offline_access`,
      state: randomCode(8),
      lang: language || '',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      clientId: MY_UPLINK_CLIENT_ID,
    });

    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    // TODO: Raise notifiation
    logger.toAppInsights(error);
  }
};

/**
 * Generates and returns the Azure B2C Change Email link.
 *
 * @async
 * @param {string} returnUri - The URL to redirect to after the password has been changed.
 * @returns {Promise<string>} The Azure B2C Change MFA link.
 */
export const getAzureB2CChangeEmailLink = async (returnUri, language, spId) => {
  try {
    const { codeChallenge } = await generateAzureB2CCodeVerifierAndCodeChallenge();
    const baseUrl = `${azureB2CBaseUrl}/${B2C_1A_EMAILCHANGE}/oauth2/v2.0/authorize`;

    const params = new URLSearchParams({
      client_id: AZURE_B2C_CLIENT_ID,
      response_type: 'code',
      response_mode: 'query',
      redirect_uri: returnUri,
      scope: `${azureB2CScope} offline_access`,
      state: randomCode(8),
      lang: language || '',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      clientId: MY_UPLINK_CLIENT_ID,
      servicePartnerId: spId ?? '',
    });

    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    // TODO: Raise notifiation
    logger.toAppInsights(error);
  }
};
